<template>
  <div class="h-[calc(100vh-178px)] flex gap-x-4">
    <ContentBlock class="basis-60" title="Filters">
      <div class="p-4 flex flex-col gap-y-4">
        <SingleObjectSelect label="Month" :options="monthOptions" v-model="proxyFilter.month" inline />
        <MultipleObjectSelect label="Weekday" :options="weekdayOptions" v-model="proxyFilter.weekdays" inline />
        <HourRangeInput label="Hours" v-model:min-value="proxyFilter.startHour"
          v-model:max-value="proxyFilter.endHour" />
        <div
          class="mt-8 flex text-zinc-50 space-x-2 items-center justify-center px-3 py-2 rounded-lg border border-accent bg-accent/75 hover:bg-accent text-sm cursor-pointer"
          @click="applyFilter">
          <span>Apply</span>
        </div>
      </div>
    </ContentBlock>
    <div class="grow rounded-lg overflow-hidden h-full relative" ref="mapElement">
      <!-- <div class="absolute bottom-4 right-4 w-24 bg-zinc-100 dark:bg-zinc-800 rounded-md shadow">
        <div class="rounded-t-md bg-zinc-200 dark:bg-zinc-700 p-2 flex justify-between items-center">
          <h5 class="font-medium text-sm">Legend</h5>
        </div>
        <div class="p-2">
          <ul>
            <li class="flex items-center justify-between" v-for="(threshold, color) in legendColors" :key="color">
              <div class="h-3.5 w-3.5 rounded-full" :style="`background-color: ${color}`"></div>
              <div>{{ threshold }}+</div>
            </li>
          </ul>
        </div>
      </div> -->
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuth0 } from "@auth0/auth0-vue";
import { format } from "date-fns";
import { cloneDeep, first, last, isEmpty, isString, range } from "lodash";
// import { scaleThreshold } from "d3-scale";
import { schemeYlOrRd } from "d3-scale-chromatic";
import AirSageMap from "@/lib/map";
import { unfurlNestedDates } from "@/lib/utils";
import HourRangeInput from "@/components/HourRangeInput.vue";
import SingleObjectSelect from "@/components/SingleObjectSelect.vue";
import MultipleObjectSelect from "@/components/MultipleObjectSelect.vue";
import ContentBlock from "@/components/ContentBlock.vue";
import { StudiesService } from "@/services";
import { useStudyStore } from "@/stores/study";
import { useAlertStore } from "@/stores/alert";

const alertStore = useAlertStore();
const studyStore = useStudyStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();
const { zones, config } = storeToRefs(studyStore);
let data;
const monthOptions = ref([]);
const weekdayOptions = [{
  name: "Monday",
  value: 1
}, {
  name: "Tuesday",
  value: 2
}, {
  name: "Wednesday",
  value: 3
}, {
  name: "Thursday",
  value: 4
}, {
  name: "Friday",
  value: 5
}, {
  name: "Saturday",
  value: 6
}, {
  name: "Sunday",
  value: 7
}];
const mounted = ref(false);
const proxyFilter = ref({
  month: "",
  weekdays: [1,2,3,4,5,6,7],
  startHour: 0,
  endHour: 23
});
const filter = ref({
  month: "",
  weekdays: [1,2,3,4,5,6,7],
  startHour: 0,
  endHour: 23
});

onMounted(async () => {
  data = await readMaterializedView("materialized_data");
  mounted.value = true;
});

onBeforeUnmount(studyStore.reset);

const POINT_LAYER_ID = "points";
let map = null;

// Heatmap tuning, calibrated against the Ada County delivery in dev/pad-harness.
//
// `heatmap-density` sums every cell under the kernel, and each zoom level quarters the number of
// ~10 m cells under a fixed-pixel kernel, so the intensity has to climb roughly 2x per zoom (hence
// the exponential interpolation) to keep the same look. A flat intensity saturates the whole city
// into one solid blob at overview zooms and fades to faint pinpricks at street level.
// The radius stays near-constant in pixels through mid zooms (growing it there merges detail
// into blobs) and only widens once cells sit isolated on screen (z16+).
const HEATMAP_INTENSITY_STOPS = [0, 0.01, 9, 0.03, 11, 0.05, 15, 0.75, 18, 5];
const HEATMAP_RADIUS_STOPS = [0, 6, 9, 20, 15, 24, 16.5, 36, 18, 50];
const HEATMAP_OPACITY = 0.85;

function applyFilter() {
  filter.value = cloneDeep(proxyFilter.value);
  loadLayers();
}

const unwatch = watch([mounted, zones, config], () => {
  if (mounted.value && !isEmpty(zones.value) && !isEmpty(config.value)) {
    monthOptions.value = unfurlNestedDates(config.value.dates || []).map((m) => ({
      name: format(m, "MMM yyyy"),
      value: format(m, "yyyyMM")
    }));
    const firstMonth = first(monthOptions.value).value;

    proxyFilter.value = {
      month: firstMonth,
      weekdays: weekdayOptions.map((wdo) => wdo.value),
      startHour: 0,
      endHour: 23
    };

    filter.value = cloneDeep(proxyFilter.value);
    loadMap();
  }
});

async function readMaterializedView(view) {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    return await StudiesService.getMaterializedView(id, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: { view }
    });
  } catch (error) {
    alertStore.alertError(error);
  }
}

function hexToRgba(hex, alpha = 1) {
  if (!isString(hex)) {
    return hex;
  }

  hex = hex.replace("#", "");

  // Convert hex values to decimal
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// const legendColors = ref([]);
// const arrayToObject = (array) => {
//   const legend = {};
//   array.unshift(0);
//   while (array.length) {
//     const color = array.shift();
//     const threshold = array.shift();
//     legend[threshold] = color;
//   }
//   return legend;
// };
// legendColors.value = arrayToObject(expression);

function generateExpression (thresholds) {
  // yellow -> red reads as a gradient on the dark basemap where single-hue reds collapse to one tone
  const colors = schemeYlOrRd[thresholds.length + 1];
  const step = 1 / (colors.length - 1);
  let expression = [];
  colors.forEach((color, index) => {
    expression.push(index * step, color);
  });
  // fade the low end in instead of snapping to the (near-white) lightest colour, which otherwise
  // reads as a bright halo around every blob on the dark basemap
  const alphaFor = (index) => [0, 0.4, 0.75][index] ?? 1;
  expression = expression.map((e, i) => (i % 2 ? hexToRgba(e, alphaFor((i - 1) / 2)) : e));
  return ["interpolate", ["linear"], ["heatmap-density"]].concat(expression);
}

function loadLayers() {
  const { id } = route.params;
  const sourceId = POINT_LAYER_ID;
  const layerId = `${POINT_LAYER_ID}-activity-density`;
  if (map) {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }

    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    map.addSource(sourceId, {
      type: "vector",
      tiles: [
        `${import.meta.env.VITE_MARTIN_URL}/get_pedestrian_activity_density/{z}/{x}/{y}?${new URLSearchParams([
          ...Object.entries({
            id,
            month: filter.value.month,
            weekdays: `{${filter.value.weekdays}}`,
            hours: `{${range(filter.value.startHour, filter.value.endHour + 1)}}`
          })
        ]).toString()}`
      ]
    });

    const expression = generateExpression(data.thresholds.map((th) => +th));
    const maxCount = Math.max(+last(data.thresholds) || 0, 1);
    map.addLayer({
      id: layerId,
      type: "heatmap",
      source: sourceId,
      "source-layer": "activity-density",
      paint: {
        "heatmap-weight": [
          "interpolate",
          ["linear"],
          ["max", ["to-number", ["get", "count"], 0], 0],
          0,
          0,
          maxCount,
          1
        ],
        "heatmap-intensity": ["interpolate", ["exponential", 2], ["zoom"], ...HEATMAP_INTENSITY_STOPS],
        "heatmap-color": expression,
        "heatmap-radius": ["interpolate", ["linear"], ["zoom"], ...HEATMAP_RADIUS_STOPS],
        "heatmap-opacity": HEATMAP_OPACITY
      }
    });
  }
}

const mapElement = ref(null);
function loadMap() {
  map = new AirSageMap(mapElement.value, {
    eventHandlers: {
      "style.load": loadLayers
    }
  });
  map.centerOn(zones.value.bbox || zones.value, { easing: () => 1 });
  unwatch();
}
</script>